import { useState, useEffect, useCallback } from 'react';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import { apiRequest } from '../../services/api';
import { getApiUrl } from '../../config/demoMode';

const API_URL = getApiUrl();
const BASE_URL = API_URL.replace(/\/api$/, '');
function getResimUrl(url) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${BASE_URL}${url}`;
}

const INP = "w-full border-2 border-gray-100 rounded-2xl px-4 py-3 text-sm font-semibold text-gray-800 focus:outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-50 bg-white transition-all placeholder-gray-300";
const LABEL = "text-xs font-bold text-gray-500 uppercase tracking-widest block mb-2";

function Section({ title, icon, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex items-center gap-3">
        <span className="text-lg">{icon}</span>
        <h3 className="font-bold text-gray-700 text-sm uppercase tracking-wide">{title}</h3>
      </div>
      <div className="p-6 space-y-5">{children}</div>
    </div>
  );
}

function Toggle({ aktif, onChange, label, desc }) {
  return (
    <label className="flex items-start gap-4 cursor-pointer group p-2 hover:bg-gray-50 rounded-xl transition-colors">
      <div className="mt-0.5 flex-shrink-0">
        <div onClick={onChange}
          className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${aktif ? 'bg-blue-600' : 'bg-gray-200'}`}>
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${aktif ? 'translate-x-5' : 'translate-x-0'}`} />
        </div>
      </div>
      <div>
        <p className="text-sm font-semibold text-gray-700">{label}</p>
        {desc && <p className="text-xs text-gray-400 mt-0.5">{desc}</p>}
      </div>
    </label>
  );
}

const RESIM_SPEC = {
  logo:        { maxW: 400,  maxH: 200,  minW: 0,   mime: 'image/png',  kalite: 0.95, bilgi: 'Önerilen: 400×200 px · PNG/WebP · şeffaf arka plan desteklenir' },
  favicon:     { maxW: 64,   maxH: 64,   minW: 0,   mime: 'image/png',  kalite: 0.95, bilgi: '64×64 px kare · PNG · tarayıcı sekme ikonu' },
  hero:        { maxW: 1920, maxH: 1080, minW: 800, mime: 'image/jpeg', kalite: 0.85, bilgi: 'Önerilen: 1920×1080 px · 16:9 yatay · JPG · maks 2 MB' },
  hakkimizda:  { maxW: 1200, maxH: 900,  minW: 600, mime: 'image/jpeg', kalite: 0.88, bilgi: 'Önerilen: 1200×900 px · 4:3 · JPG · maks 1.5 MB' },
  og:          { maxW: 1200, maxH: 630,  minW: 0,   mime: 'image/jpeg', kalite: 0.88, bilgi: '1200×630 px · OG/sosyal medya standardı · JPG' },
};

function isleGorsel(dosya, spec) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(dosya);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const { naturalWidth: w, naturalHeight: h } = img;
      const kucuk = spec.minW > 0 && w < spec.minW;
      const buyuk = w > spec.maxW || h > spec.maxH;

      if (!buyuk) {
        resolve({ dosya, kucuk });
        return;
      }

      const oran = Math.min(spec.maxW / w, spec.maxH / h);
      const tw = Math.round(w * oran);
      const th = Math.round(h * oran);
      const canvas = document.createElement('canvas');
      canvas.width = tw;
      canvas.height = th;
      const ctx = canvas.getContext('2d');
      if (spec.mime === 'image/jpeg') {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, tw, th);
      }
      ctx.drawImage(img, 0, 0, tw, th);
      const ext = spec.mime === 'image/jpeg' ? '.jpg' : '.png';
      const yeniAd = dosya.name.replace(/\.[^.]+$/, ext);
      canvas.toBlob(
        (blob) => resolve({ dosya: new File([blob], yeniAd, { type: spec.mime }), kucuk }),
        spec.mime,
        spec.kalite
      );
    };
    img.onerror = () => reject(new Error('Görsel okunamadı'));
    img.src = url;
  });
}

function ResimYukleBtn({ tip, mevcutUrl, onUploaded, label }) {
  const [yukleniyor, setYukleniyor] = useState(false);
  const [uyari, setUyari] = useState('');
  const spec = RESIM_SPEC[tip] || RESIM_SPEC.hero;

  async function yukle(e) {
    const secilen = e.target.files[0];
    if (!secilen) return;
    setYukleniyor(true);
    setUyari('');
    try {
      const { dosya, kucuk } = await isleGorsel(secilen, spec);
      if (kucuk) setUyari('Görsel önerilen boyutun altında, bulanık görünebilir.');
      const fd = new FormData();
      fd.append('dosya', dosya);
      const res = await fetch(`${API_URL}/website/resim/${tip}`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) throw new Error('Yükleme başarısız');
      const data = await res.json();
      onUploaded(data.url);
    } catch (err) {
      alert('Görsel yüklenemedi: ' + err.message);
    } finally {
      setYukleniyor(false);
    }
  }

  return (
    <div className="space-y-2">
      <label className={LABEL}>{label}</label>
      <div className="flex items-center gap-3">
        {mevcutUrl && (
          <img src={getResimUrl(mevcutUrl)} alt="" className="w-14 h-14 object-contain rounded-xl border border-gray-100 bg-gray-50" />
        )}
        <label className="flex-1 cursor-pointer">
          <div className="border-2 border-dashed border-gray-200 rounded-xl p-3 text-center hover:border-blue-300 hover:bg-blue-50 transition-all">
            <p className="text-xs font-semibold text-gray-500">
              {yukleniyor ? 'İşleniyor...' : (mevcutUrl ? 'Değiştir' : 'Görsel Seç')}
            </p>
          </div>
          <input type="file" accept="image/*" className="hidden" onChange={yukle} disabled={yukleniyor} />
        </label>
        {mevcutUrl && (
          <button onClick={() => onUploaded('')} className="text-xs text-red-400 hover:text-red-600 px-2 py-1">Kaldır</button>
        )}
      </div>
      <p className="text-xs text-gray-400 leading-relaxed">
        ℹ️ {spec.bilgi} · Büyük görseller otomatik küçültülür
      </p>
      {uyari && (
        <p className="text-xs text-amber-500 font-medium">⚠️ {uyari}</p>
      )}
      {mevcutUrl && (
        <div>
          <label className={LABEL}>URL (elle düzenle)</label>
          <input className={INP} value={mevcutUrl} onChange={e => onUploaded(e.target.value)} placeholder="https://..." />
        </div>
      )}
    </div>
  );
}

function trimSonSatirlar(hucreler, kolonSayisi) {
  let son = hucreler.length;
  while (son >= kolonSayisi) {
    const bas = son - kolonSayisi;
    if (hucreler.slice(bas, son).every(h => h === null)) son = bas;
    else break;
  }
  return hucreler.slice(0, son);
}

function HucreGorselYukle({ url, onUploaded }) {
  const [yukleniyor, setYukleniyor] = useState(false);

  async function yukle(e) {
    const dosya = e.target.files[0];
    if (!dosya) return;
    setYukleniyor(true);
    try {
      const { dosya: islendi } = await isleGorsel(dosya, RESIM_SPEC.hakkimizda);
      const fd = new FormData();
      fd.append('dosya', islendi);
      const res = await fetch(`${API_URL}/website/resim/hakkimizda`, { method: 'POST', credentials: 'include', body: fd });
      if (!res.ok) throw new Error();
      const { url: yeniUrl } = await res.json();
      onUploaded(yeniUrl);
    } catch (e) { alert(e?.message || 'Görsel yüklenemedi.'); }
    finally { setYukleniyor(false); }
  }

  if (url) return (
    <div className="space-y-2">
      <img src={getResimUrl(url)} alt="" className="w-full h-28 object-cover rounded-xl border border-gray-100" />
      <div className="flex gap-2">
        <label className="flex-1 cursor-pointer">
          <div className="text-center py-1.5 border-2 border-dashed border-gray-200 rounded-lg text-xs font-semibold text-gray-400 hover:border-blue-300 hover:text-blue-500 transition-all">
            {yukleniyor ? 'Yükleniyor...' : 'Değiştir'}
          </div>
          <input type="file" accept="image/*" className="hidden" onChange={yukle} disabled={yukleniyor} />
        </label>
        <button onClick={() => onUploaded('')} className="text-xs text-red-400 hover:text-red-600 px-2">Kaldır</button>
      </div>
    </div>
  );

  return (
    <label className="block cursor-pointer">
      <div className="min-h-[80px] flex flex-col items-center justify-center gap-1 border-2 border-dashed border-gray-200 rounded-xl hover:border-blue-300 hover:bg-blue-50 transition-all">
        <p className="text-xs font-bold text-gray-400">{yukleniyor ? 'Yükleniyor...' : '+ Görsel Seç'}</p>
      </div>
      <input type="file" accept="image/*" className="hidden" onChange={yukle} disabled={yukleniyor} />
    </label>
  );
}

function GridEditor({ value, onChange }) {
  const parseGrid = (v) => {
    try {
      const p = JSON.parse(v || 'null');
      if (!p) return { kolonSayisi: 2, hucreler: [] };
      if (Array.isArray(p)) return { kolonSayisi: 1, hucreler: p };
      return { kolonSayisi: p.kolonSayisi || 2, hucreler: p.hucreler || [] };
    } catch { return { kolonSayisi: 2, hucreler: [] }; }
  };

  const [grid, setGrid] = useState(() => parseGrid(value));
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  function update(g) { setGrid(g); onChange(JSON.stringify(g)); }

  function setKolonSayisi(n) {
    const h = [...grid.hucreler];
    const rem = h.length % n;
    if (rem !== 0) for (let i = 0; i < n - rem; i++) h.push(null);
    update({ kolonSayisi: n, hucreler: h });
  }

  function satirEkle() {
    update({ ...grid, hucreler: [...grid.hucreler, ...Array(grid.kolonSayisi).fill(null)] });
  }

  function hucreGuncelle(idx, deger) {
    const h = [...grid.hucreler];
    h[idx] = deger;
    update({ ...grid, hucreler: h });
  }

  function hucreSil(idx) {
    const h = [...grid.hucreler];
    h[idx] = null;
    update({ ...grid, hucreler: trimSonSatirlar(h, grid.kolonSayisi) });
  }

  function icerikEkle(tip) {
    const h = [...grid.hucreler];
    const bosIdx = h.findIndex(x => x === null);
    const yeni = tip === 'metin' ? { tip: 'metin', icerik: '' } : { tip: 'gorsel', url: '' };
    if (bosIdx !== -1) {
      h[bosIdx] = yeni;
      update({ ...grid, hucreler: h });
    } else {
      const satir = Array(grid.kolonSayisi).fill(null);
      satir[0] = yeni;
      update({ ...grid, hucreler: [...h, ...satir] });
    }
  }

  function handleDrop(targetIdx) {
    if (dragIdx === null || dragIdx === targetIdx) { setDragIdx(null); setDragOver(null); return; }
    const h = [...grid.hucreler];
    [h[targetIdx], h[dragIdx]] = [h[dragIdx], h[targetIdx]];
    update({ ...grid, hucreler: trimSonSatirlar(h, grid.kolonSayisi) });
    setDragIdx(null); setDragOver(null);
  }

  const gridColsCls = grid.kolonSayisi === 1 ? 'grid-cols-1' : grid.kolonSayisi === 2 ? 'grid-cols-2' : 'grid-cols-3';

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-0.5 bg-gray-100 rounded-xl p-1">
          {[1, 2, 3].map(n => (
            <button key={n} onClick={() => setKolonSayisi(n)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                grid.kolonSayisi === n ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-400 hover:text-gray-600'
              }`}>
              {n} Sütun
            </button>
          ))}
        </div>
        <button onClick={() => icerikEkle('metin')}
          className="px-3 py-1.5 border-2 border-dashed border-gray-200 rounded-xl text-xs font-bold text-gray-400 hover:border-blue-300 hover:text-blue-500 transition-all">
          + Paragraf
        </button>
        <button onClick={() => icerikEkle('gorsel')}
          className="px-3 py-1.5 border-2 border-dashed border-gray-200 rounded-xl text-xs font-bold text-gray-400 hover:border-blue-300 hover:text-blue-500 transition-all">
          + Görsel
        </button>
        <button onClick={satirEkle}
          className="px-3 py-1.5 border-2 border-dashed border-gray-200 rounded-xl text-xs font-bold text-gray-400 hover:border-gray-400 hover:text-gray-600 transition-all">
          + Satır
        </button>
      </div>

      {grid.hucreler.length === 0 ? (
        <p className="text-xs text-gray-400 text-center py-8 border-2 border-dashed border-gray-100 rounded-2xl">
          Yukarıdan paragraf / görsel ekleyin veya "+ Satır" ile boş satır oluşturun.
        </p>
      ) : (
        <div className={`grid ${gridColsCls} gap-3`}>
          {grid.hucreler.map((hucre, idx) => (
            <div
              key={idx}
              onDragOver={(e) => { e.preventDefault(); setDragOver(idx); }}
              onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(null); }}
              onDrop={() => handleDrop(idx)}
              className={[
                'min-h-[160px] border-2 rounded-2xl transition-all',
                dragOver === idx && dragIdx !== idx
                  ? 'border-blue-400 bg-blue-50'
                  : hucre
                    ? 'border-gray-200 bg-white'
                    : 'border-dashed border-gray-200 bg-gray-50',
                dragIdx === idx ? 'opacity-40' : '',
              ].join(' ')}
            >
              {hucre ? (
                <div className="h-full flex flex-col">
                  <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-100 rounded-t-2xl">
                    <span
                      draggable
                      onDragStart={() => setDragIdx(idx)}
                      onDragEnd={() => { setDragIdx(null); setDragOver(null); }}
                      className="flex items-center gap-1.5 text-xs font-bold text-gray-400 cursor-grab active:cursor-grabbing select-none hover:text-gray-600"
                      title="Sürükleyerek taşı"
                    >
                      <span className="text-sm">⠿</span>
                      {hucre.tip === 'metin' ? 'Paragraf' : 'Görsel'}
                    </span>
                    <div className="flex items-center">
                      <button
                        onClick={() => hucreGuncelle(idx, hucre.tip === 'metin' ? { tip: 'gorsel', url: '' } : { tip: 'metin', icerik: '' })}
                        className="text-xs text-gray-300 hover:text-blue-500 px-2 py-1 transition-colors font-bold"
                        title={hucre.tip === 'metin' ? 'Görsele çevir' : 'Paragrafa çevir'}
                      >⇄</button>
                      <button onClick={() => hucreSil(idx)}
                        className="text-xs text-gray-300 hover:text-red-500 px-2 py-1 transition-colors font-bold">✕</button>
                    </div>
                  </div>
                  <div className="p-3 flex-1">
                    {hucre.tip === 'metin' ? (
                      <textarea
                        className="w-full h-full min-h-[100px] text-sm text-gray-700 bg-transparent border-0 outline-none resize-none placeholder-gray-300 leading-relaxed"
                        value={hucre.icerik}
                        onChange={e => hucreGuncelle(idx, { ...hucre, icerik: e.target.value })}
                        placeholder="Paragraf metnini yazın..."
                      />
                    ) : (
                      <HucreGorselYukle url={hucre.url} onUploaded={url => hucreGuncelle(idx, { ...hucre, url })} />
                    )}
                  </div>
                </div>
              ) : (
                <div className="h-full min-h-[160px] flex flex-col items-center justify-center gap-1 select-none text-center px-3">
                  <span className="text-2xl text-gray-200">⊕</span>
                  <span className="text-xs text-gray-300 font-medium">Boş hücre</span>
                  <span className="text-xs text-gray-200">buraya sürükleyebilirsiniz</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const VARSAYILAN = {
  slug: '',
  domain: '',
  logoUrl: '',
  faviconUrl: '',
  primaryColor: '#1a56db',
  secondaryColor: '#ffffff',
  heroBaslik: '',
  heroAltBaslik: '',
  heroResimUrl: '',
  hakkimizdaBaslik: 'Hakkımızda',
  hakkimizdaMetin: '',
  hakkimizdaResimUrl: '',
  hakkimizdaIcerikJson: '[]',
  iletisimAdres: '',
  iletisimTelefon: '',
  iletisimEmail: '',
  googleMapsEmbed: '',
  galeriJson: '[]',
  referanslarJson: '[]',
  sanalPosAktif: false,
  onOdemePct: 0,
  ibanBilgisi: '',
  minGeceSayisi: 1,
  kisiSayisiDevreDisi: false,
  misafirBilgileriZorunlu: false,
  musaitlikTablosuGoster: true,
  formKapali: false,
  tesekkurMesaji: 'Rezervasyon talebiniz alındı. En kısa sürede sizinle iletişime geçeceğiz.',
  aktifMi: true,
};

export default function WebSiteAyarlari() {
  const [yukleniyor, setYukleniyor] = useState(true);
  const [form, setForm] = useState(VARSAYILAN);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [mesaj, setMesaj] = useState('');
  const [slugHata, setSlugHata] = useState('');

  const yukle = useCallback(async () => {
    setYukleniyor(true);
    try {
      const data = await apiRequest('website');
      if (data) setForm({
        slug: data.slug || '',
        domain: data.domain || '',
        logoUrl: data.logoUrl || '',
        faviconUrl: data.faviconUrl || '',
        primaryColor: data.primaryColor || '#1a56db',
        secondaryColor: data.secondaryColor || '#ffffff',
        heroBaslik: data.heroBaslik || '',
        heroAltBaslik: data.heroAltBaslik || '',
        heroResimUrl: data.heroResimUrl || '',
        hakkimizdaBaslik: data.hakkimizdaBaslik || 'Hakkımızda',
        hakkimizdaMetin: data.hakkimizdaMetin || '',
        hakkimizdaResimUrl: data.hakkimizdaResimUrl || '',
        hakkimizdaIcerikJson: data.hakkimizdaIcerikJson || '[]',
        iletisimAdres: data.iletisimAdres || '',
        iletisimTelefon: data.iletisimTelefon || '',
        iletisimEmail: data.iletisimEmail || '',
        googleMapsEmbed: data.googleMapsEmbed || '',
        galeriJson: data.galeriJson || '[]',
        referanslarJson: data.referanslarJson || '[]',
        sanalPosAktif: data.sanalPosAktif || false,
        onOdemePct: data.onOdemePct || 0,
        ibanBilgisi: data.ibanBilgisi || '',
        minGeceSayisi: data.minGeceSayisi || 1,
        kisiSayisiDevreDisi: data.kisiSayisiDevreDisi || false,
        misafirBilgileriZorunlu: data.misafirBilgileriZorunlu || false,
        musaitlikTablosuGoster: data.musaitlikTablosuGoster ?? true,
        formKapali: data.formKapali || false,
        tesekkurMesaji: data.tesekkurMesaji || VARSAYILAN.tesekkurMesaji,
        aktifMi: data.aktifMi ?? true,
      });
    } catch { }
    setYukleniyor(false);
  }, []);

  useEffect(() => { yukle(); }, [yukle]);

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); }

  async function kontrolSlug(slug) {
    if (!slug) { setSlugHata(''); return; }
    try {
      const res = await apiRequest(`website/slug-kontrol?slug=${encodeURIComponent(slug)}`);
      setSlugHata(res?.mevcut ? 'Bu slug zaten kullanımda.' : '');
    } catch { }
  }

  async function kaydet() {
    if (!form.slug) { setMesaj('❌ Slug alanı zorunludur.'); setTimeout(() => setMesaj(''), 3000); return; }
    if (slugHata) { setMesaj('❌ Slug hatası giderilinceye kadar kaydedemezsiniz.'); setTimeout(() => setMesaj(''), 3000); return; }
    setKaydediliyor(true);
    try {
      await apiRequest('website/ayar', 'PUT', form);
      setMesaj('✅ Ayarlar kaydedildi.');
    } catch (e) {
      setMesaj('❌ ' + (e.message || 'Kaydedilemedi.'));
    } finally {
      setKaydediliyor(false);
      setTimeout(() => setMesaj(''), 3000);
    }
  }

  if (yukleniyor) return <LoadingSpinner fullPage text="Web sitesi ayarları yükleniyor..." />;

  return (
    <div className="p-4 md:p-6 space-y-6 min-h-screen bg-gray-50">
      <PageHeader icon="🌐" iconColor="from-blue-600 to-indigo-700" iconShadow="shadow-blue-200"
        title="Web Sitesi — Genel Ayarlar"
        subtitle="Marka kimliği, içerikler ve rezervasyon formu ayarları"
      >
        <div className="flex items-center gap-3">
          {form.slug && (
            <a href={`http://localhost:3001/${form.slug}`} target="_blank" rel="noreferrer"
              className="text-xs font-bold text-blue-600 bg-blue-50 px-4 py-2 rounded-xl border border-blue-200 hover:bg-blue-100 transition-all">
              🔗 Önizle
            </a>
          )}
          <button onClick={kaydet} disabled={kaydediliyor}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-6 py-2 rounded-xl text-sm font-bold transition-all">
            {kaydediliyor ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </PageHeader>

      {mesaj && (
        <div className={`p-4 rounded-xl text-sm font-semibold ${mesaj.startsWith('✅') ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {mesaj}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

        {/* Sol */}
        <div className="space-y-6">

          <Section title="Site Kimliği" icon="🔗">
            <div>
              <label className={LABEL}>Slug (URL kısa adı) *</label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-400 font-mono">siten.com/</span>
                <input className={`${INP} flex-1 ${slugHata ? 'border-red-400' : ''}`}
                  value={form.slug} placeholder="aquagarden"
                  onChange={e => {
                    const v = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                    set('slug', v);
                    kontrolSlug(v);
                  }}
                />
              </div>
              {slugHata && <p className="text-xs text-red-500 mt-1">{slugHata}</p>}
              <p className="text-xs text-gray-400 mt-1">Sadece küçük harf, rakam ve tire. Örn: aqua-garden</p>
            </div>
            <div>
              <label className={LABEL}>Özel Domain (isteğe bağlı)</label>
              <input className={INP} value={form.domain} placeholder="aquagardenotel.com.tr"
                onChange={e => set('domain', e.target.value)} />
              <p className="text-xs text-gray-400 mt-1">Domain henüz alınmadıysa boş bırakın.</p>
            </div>
            <div className="flex items-center gap-4">
              <Toggle aktif={form.aktifMi} onChange={() => set('aktifMi', !form.aktifMi)}
                label="Web Sitesi Aktif"
                desc="Kapatıldığında public API 404 döner." />
            </div>
          </Section>

          <Section title="Marka & Görsel Kimlik" icon="🎨">
            <ResimYukleBtn tip="logo" label="Logo" mevcutUrl={form.logoUrl} onUploaded={v => set('logoUrl', v)} />
            <ResimYukleBtn tip="favicon" label="Favicon (32×32 px)" mevcutUrl={form.faviconUrl} onUploaded={v => set('faviconUrl', v)} />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={LABEL}>Ana Renk</label>
                <div className="flex items-center gap-3">
                  <input type="color" value={form.primaryColor} onChange={e => set('primaryColor', e.target.value)}
                    className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5" />
                  <input className={`${INP} flex-1 font-mono text-xs`} value={form.primaryColor}
                    onChange={e => set('primaryColor', e.target.value)} placeholder="#1a56db" />
                </div>
              </div>
              <div>
                <label className={LABEL}>Vurgu Rengi</label>
                <div className="flex items-center gap-3">
                  <input type="color" value={form.secondaryColor} onChange={e => set('secondaryColor', e.target.value)}
                    className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5" />
                  <input className={`${INP} flex-1 font-mono text-xs`} value={form.secondaryColor}
                    onChange={e => set('secondaryColor', e.target.value)} placeholder="#ffffff" />
                </div>
              </div>
            </div>
          </Section>

          <Section title="Ana Sayfa (Hero)" icon="🖼️">
            <ResimYukleBtn tip="hero" label="Hero Arkaplan Görseli" mevcutUrl={form.heroResimUrl} onUploaded={v => set('heroResimUrl', v)} />
            <div>
              <label className={LABEL}>Başlık</label>
              <input className={INP} value={form.heroBaslik} placeholder="Muhteşem Bir Konaklama Deneyimi"
                onChange={e => set('heroBaslik', e.target.value)} />
            </div>
            <div>
              <label className={LABEL}>Alt Başlık</label>
              <input className={INP} value={form.heroAltBaslik} placeholder="Hayal ettiğiniz tatil için..."
                onChange={e => set('heroAltBaslik', e.target.value)} />
            </div>
          </Section>

          <Section title="Hakkımızda" icon="ℹ️">
            <div>
              <label className={LABEL}>Sayfa Başlığı</label>
              <input className={INP} value={form.hakkimizdaBaslik} placeholder="Hakkımızda"
                onChange={e => set('hakkimizdaBaslik', e.target.value)} />
            </div>

            <div className="border-t border-gray-100 pt-5">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Ana Sayfa Önizleme</p>
              <div className="space-y-4">
                <div>
                  <label className={LABEL}>Kısa Açıklama</label>
                  <textarea className={`${INP} resize-none`} rows={3} value={form.hakkimizdaMetin}
                    placeholder="Ana sayfada görünecek kısa özet metin..."
                    onChange={e => set('hakkimizdaMetin', e.target.value)} />
                </div>
                <ResimYukleBtn tip="hakkimizda" label="Önizleme Görseli" mevcutUrl={form.hakkimizdaResimUrl} onUploaded={v => set('hakkimizdaResimUrl', v)} />
              </div>
            </div>

            <div className="border-t border-gray-100 pt-5">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Hakkımızda Sayfası İçeriği</p>
              <p className="text-xs text-gray-400 mb-4">Sütun sayısını seçin, paragraf ve görsel ekleyin. Hücreleri <strong>⠿</strong> tutucusundan sürükleyip bırakarak yeniden düzenleyin.</p>
              <GridEditor
                value={form.hakkimizdaIcerikJson}
                onChange={v => set('hakkimizdaIcerikJson', v)}
              />
            </div>
          </Section>

        </div>

        {/* Sağ */}
        <div className="space-y-6">

          <Section title="İletişim Bilgileri" icon="📍">
            <div>
              <label className={LABEL}>Adres</label>
              <textarea className={`${INP} resize-none`} rows={2} value={form.iletisimAdres}
                placeholder="Mahalle Caddesi No:1, İlçe / Şehir"
                onChange={e => set('iletisimAdres', e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={LABEL}>Telefon</label>
                <input className={INP} value={form.iletisimTelefon} placeholder="+90 555 000 00 00"
                  onChange={e => set('iletisimTelefon', e.target.value)} />
              </div>
              <div>
                <label className={LABEL}>E-posta</label>
                <input className={INP} value={form.iletisimEmail} placeholder="info@otel.com"
                  onChange={e => set('iletisimEmail', e.target.value)} />
              </div>
            </div>
            <div>
              <label className={LABEL}>Google Maps Embed Kodu</label>
              <textarea className={`${INP} resize-none font-mono text-xs`} rows={3} value={form.googleMapsEmbed}
                placeholder={'<iframe src="https://maps.google.com/maps?..." ...></iframe>'}
                onChange={e => set('googleMapsEmbed', e.target.value)} />
              <p className="text-xs text-gray-400 mt-1">Google Maps → Paylaş → Haritayı Göm → HTML kodu yapıştırın.</p>
            </div>
          </Section>

          <Section title="Oda Fotoğrafları" icon="🛏️">
            <OdaFotograflari />
          </Section>

          <Section title="Galeri" icon="🖼️">
            <GaleriYonetici galeriJson={form.galeriJson} onChange={v => set('galeriJson', v)} />
          </Section>

          <Section title="Misafir Yorumları" icon="⭐">
            <ReferanslarYonetici referanslarJson={form.referanslarJson} onChange={v => set('referanslarJson', v)} />
          </Section>

          <Section title="Rezervasyon Formu Ayarları" icon="📋">
            <div>
              <label className={LABEL}>Minimum Gece Sayısı</label>
              <div className="flex items-center gap-3">
                <button onClick={() => set('minGeceSayisi', Math.max(1, form.minGeceSayisi - 1))}
                  className="w-10 h-10 rounded-xl border-2 border-gray-200 text-gray-600 font-bold hover:bg-gray-100 transition-all">－</button>
                <span className="text-xl font-bold text-gray-800 w-8 text-center">{form.minGeceSayisi}</span>
                <button onClick={() => set('minGeceSayisi', form.minGeceSayisi + 1)}
                  className="w-10 h-10 rounded-xl border-2 border-gray-200 text-gray-600 font-bold hover:bg-gray-100 transition-all">＋</button>
              </div>
            </div>
            <div className="space-y-3">
              <Toggle aktif={form.kisiSayisiDevreDisi} onChange={() => set('kisiSayisiDevreDisi', !form.kisiSayisiDevreDisi)}
                label="Kişi sayısını devre dışı bırak" desc="Fiyatlandırma oda bazlı sabit olur." />
              <Toggle aktif={form.misafirBilgileriZorunlu} onChange={() => set('misafirBilgileriZorunlu', !form.misafirBilgileriZorunlu)}
                label="Misafir bilgileri zorunlu" desc="İsim, telefon zorunlu alan olur." />
              <Toggle aktif={form.musaitlikTablosuGoster} onChange={() => set('musaitlikTablosuGoster', !form.musaitlikTablosuGoster)}
                label="Müsaitlik takvimini göster" desc="Misafir uygun tarihleri görebilir." />
              <Toggle aktif={form.formKapali} onChange={() => set('formKapali', !form.formKapali)}
                label="Rezervasyon formunu kapat" desc="Sezon dışı veya bakımda form kapalı görünür." />
            </div>
            {form.formKapali && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs font-semibold text-red-600">
                ⚠️ Form şu an kapalı — misafirler rezervasyon yapamaz.
              </div>
            )}
          </Section>

          <Section title="Ödeme Ayarları" icon="💳">
            <Toggle aktif={form.sanalPosAktif} onChange={() => set('sanalPosAktif', !form.sanalPosAktif)}
              label="Sanal POS / Online Ödeme" desc="Rezervasyon anında kredi kartı tahsilatı." />
            {form.sanalPosAktif && (
              <div>
                <label className={LABEL}>Ön Ödeme Yüzdesi: %{form.onOdemePct}</label>
                <input type="range" min={0} max={100} value={form.onOdemePct}
                  onChange={e => set('onOdemePct', +e.target.value)}
                  className="w-full accent-blue-600" />
              </div>
            )}
            <div>
              <label className={LABEL}>IBAN / Havale Bilgileri</label>
              <textarea className={`${INP} resize-none font-mono text-xs`} rows={3} value={form.ibanBilgisi}
                placeholder="Banka: Garanti&#10;IBAN: TR00 0000 0000 0000 0000 0000 00"
                onChange={e => set('ibanBilgisi', e.target.value)} />
            </div>
          </Section>

          <Section title="Teşekkür Mesajı" icon="✉️">
            <div>
              <label className={LABEL}>Rezervasyon sonrası gösterilecek mesaj</label>
              <textarea className={`${INP} resize-none`} rows={3} value={form.tesekkurMesaji}
                placeholder="Rezervasyon talebiniz alındı..."
                onChange={e => set('tesekkurMesaji', e.target.value)} />
            </div>
          </Section>

        </div>
      </div>
    </div>
  );
}

const ODA_OZELLIK_IKONLARI = {
  wifi:          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M1.5 8.5a15 15 0 0121 0M5 12.5a11 11 0 0114 0M8.5 16.5a7 7 0 017 0M12 20.5h.01"/></svg>,
  klima:         <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M12 2v20M12 2l-2 2M12 2l2 2M12 22l-2-2M12 22l2-2M2 12h20M2 12l2-2M2 12l2 2M22 12l-2-2M22 12l-2 2M5.636 5.636l12.728 12.728M5.636 5.636l.707 2.828M5.636 5.636l2.828.707M18.364 18.364l-.707-2.828M18.364 18.364l-2.828-.707M5.636 18.364l2.828-.707M5.636 18.364l.707-2.828M18.364 5.636l-.707 2.828M18.364 5.636l-2.828.707"/></svg>,
  jakuzi:        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M3 17h18M3 17a2 2 0 01-2-2v-1h20v1a2 2 0 01-2 2M3 17v2a1 1 0 001 1h16a1 1 0 001-1v-2M7 14V9a2 2 0 114 0v1h2V9a2 2 0 114 0v5M9 5.5V4M12 5.5V4M15 5.5V4"/></svg>,
  balkon:        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M3 21h18M9 21V9M15 21V9M3 9h18M12 3l9 6H3l9-6z"/></svg>,
  deniz_manzara: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M2 17c1.5-2 3-2 4.5 0s3 2 4.5 0 3-2 4.5 0 3 2 4.5 0M2 12c1.5-2 3-2 4.5 0s3 2 4.5 0 3-2 4.5 0 3 2 4.5 0M12 3a3 3 0 100 6 3 3 0 000-6zM12 3v2M8 5l1.5 1.5M16 5l-1.5 1.5"/></svg>,
  minibar:       <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M9 3H5l1 9h8l1-9h-4M9 3v2M15 3v2M7 12c0 5 2 9 5 9s5-4 5-9M10 16h4"/></svg>,
  tv:            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M9 17l-1 3H8m8-3l1 3h-1M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>,
  sac_kurutma:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M9.5 3A6.5 6.5 0 003 9.5c0 3.59 2.91 6.5 6.5 6.5H12v3a2 2 0 004 0V6a3 3 0 00-6-.5M17 9h3M17 12h2"/></svg>,
  kahve:         <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M18 8h1a4 4 0 010 8h-1M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8zM6 1v3M10 1v3M14 1v3"/></svg>,
  kasa:          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>,
  teras:         <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M12 3v1M4.22 4.22l.71.71M1 12h1M4.22 19.78l.71-.71M12 21v-1M19.78 19.78l-.71-.71M23 12h-1M19.78 4.22l-.71.71M12 8a4 4 0 100 8 4 4 0 000-8z"/></svg>,
  ozel_banyo:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M4 12h16M4 12a8 8 0 0116 0M4 12V6a2 2 0 012-2h1M4 12v8h16v-8M8 20v1M16 20v1M10 16v1M14 16v1M12 16v1"/></svg>,
  calısma_masasi:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M20 7H4a2 2 0 00-2 2v10h20V9a2 2 0 00-2-2zM8 21v-2M16 21v-2M1 19h22M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>,
  ucretsiz_park: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M5 17H3v-6l2-5h14l2 5v6h-2M5 17a2 2 0 104 0m6 0a2 2 0 104 0M5 11h14M9 11V8M15 11V8"/></svg>,
  havuz_erisim:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M2 20c1.5-2 3-2 4.5 0s3 2 4.5 0 3-2 4.5 0 3 2 4.5 0M2 16c1.5-2 3-2 4.5 0s3 2 4.5 0 3-2 4.5 0 3 2 4.5 0M12 4a2 2 0 100 4 2 2 0 000-4zM12 8v4l3 3"/></svg>,
  spa:           <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M12 3c-1.5 3-5 5-8 5 0 4 2 8 8 10 6-2 8-6 8-10-3 0-6.5-2-8-5zM12 3v14M9 10c1 1 2 1.5 3 1.5M15 10c-1 1-2 1.5-3 1.5"/></svg>,
};

const ODA_OZELLIKLERI = [
  { id: 'wifi',           label: 'Wi-Fi' },
  { id: 'klima',          label: 'Klima' },
  { id: 'jakuzi',         label: 'Jakuzi' },
  { id: 'balkon',         label: 'Balkon' },
  { id: 'deniz_manzara',  label: 'Deniz Manzarası' },
  { id: 'minibar',        label: 'Minibar' },
  { id: 'tv',             label: 'TV' },
  { id: 'sac_kurutma',    label: 'Saç Kurutma' },
  { id: 'kahve',          label: 'Kahve Makinesi' },
  { id: 'kasa',           label: 'Oda Kasası' },
  { id: 'teras',          label: 'Teras' },
  { id: 'ozel_banyo',     label: 'Özel Banyo' },
  { id: 'calısma_masasi', label: 'Çalışma Masası' },
  { id: 'ucretsiz_park',  label: 'Ücretsiz Otopark' },
  { id: 'havuz_erisim',   label: 'Havuz Erişimi' },
  { id: 'spa',            label: 'Spa Erişimi' },
];

function OdaFotograflari() {
  const [tipler, setTipler] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [yukleniyorId, setYukleniyorId] = useState(null);

  useEffect(() => {
    apiRequest('OdaTipi')
      .then(data => setTipler(data || []))
      .catch(() => {})
      .finally(() => setYukleniyor(false));
  }, []);

  async function resimYukle(tip, e) {
    const dosya = e.target.files[0];
    if (!dosya) return;
    setYukleniyorId(tip.odaTipiId);
    try {
      const fd = new FormData();
      fd.append('dosya', dosya);
      const res = await fetch(`${API_URL}/OdaTipi/${tip.odaTipiId}/resim`, { method: 'POST', credentials: 'include', body: fd });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setTipler(prev => prev.map(t => t.odaTipiId === tip.odaTipiId ? { ...t, resimlerJson: JSON.stringify(data.resimler) } : t));
    } catch (e) { alert(e?.message || 'Fotoğraf yüklenemedi.'); }
    finally { setYukleniyorId(null); e.target.value = ''; }
  }

  async function resimSil(tip, url) {
    try {
      const res = await fetch(`${API_URL}/OdaTipi/${tip.odaTipiId}/resim?url=${encodeURIComponent(url)}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setTipler(prev => prev.map(t => t.odaTipiId === tip.odaTipiId ? { ...t, resimlerJson: JSON.stringify(data.resimler) } : t));
    } catch (e) { alert(e?.message || 'Fotoğraf silinemedi.'); }
  }

  async function aciklamaKaydet(tip, yeniAciklama) {
    try {
      const res = await fetch(`${API_URL}/OdaTipi/${tip.odaTipiId}/aciklama`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(yeniAciklama),
      });
      if (!res.ok) throw new Error();
      setTipler(prev => prev.map(t => t.odaTipiId === tip.odaTipiId ? { ...t, aciklama: yeniAciklama } : t));
    } catch (e) { alert(e?.message || 'Açıklama kaydedilemedi.'); }
  }

  async function ozellikToggle(tip, ozellikId) {
    const mevcutlar = (() => { try { return tip.ozelliklerJson ? JSON.parse(tip.ozelliklerJson) : []; } catch { return []; } })();
    const yeni = mevcutlar.includes(ozellikId) ? mevcutlar.filter(x => x !== ozellikId) : [...mevcutlar, ozellikId];
    const yeniJson = JSON.stringify(yeni);
    try {
      const res = await fetch(`${API_URL}/OdaTipi/${tip.odaTipiId}/ozellikler`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: yeniJson,
      });
      if (!res.ok) throw new Error();
      setTipler(prev => prev.map(t => t.odaTipiId === tip.odaTipiId ? { ...t, ozelliklerJson: yeniJson } : t));
    } catch (e) { alert(e?.message || 'Özellik kaydedilemedi.'); }
  }

  if (yukleniyor) return <p className="text-xs text-gray-400 text-center py-4">Yükleniyor...</p>;
  if (tipler.length === 0) return <p className="text-xs text-gray-400 text-center py-4">Henüz oda tipi tanımlanmamış.</p>;

  return (
    <div className="space-y-6">
      {tipler.map(tip => {
        const resimler = (() => { try { return tip.resimlerJson ? JSON.parse(tip.resimlerJson) : []; } catch { return []; } })();
        const ozellikler = (() => { try { return tip.ozelliklerJson ? JSON.parse(tip.ozelliklerJson) : []; } catch { return []; } })();
        return (
          <div key={tip.odaTipiId} className="border-2 border-gray-100 rounded-2xl overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
              <span className="text-sm font-bold text-gray-700">{tip.ad}</span>
            </div>
            <div className="p-4 space-y-5">

              {/* Açıklama */}
              <div>
                <p className={LABEL}>Açıklama</p>
                <textarea
                  className={`${INP} resize-none`}
                  rows={3}
                  defaultValue={tip.aciklama || ''}
                  placeholder="Oda tipi hakkında kısa açıklama..."
                  onBlur={e => {
                    const val = e.target.value.trim();
                    if (val !== (tip.aciklama || '').trim()) aciklamaKaydet(tip, val || null);
                  }}
                />
                <p className="text-xs text-gray-400 mt-1">Odak kaybedince otomatik kaydedilir.</p>
              </div>

              {/* Fotoğraflar */}
              <div>
                <p className={LABEL}>Fotoğraflar</p>
                <div className="grid grid-cols-3 gap-2">
                  {resimler.map((url, i) => (
                    <div key={i} className="relative group aspect-video bg-gray-100 rounded-xl overflow-hidden">
                      <img src={getResimUrl(url)} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => resimSil(tip, url)}
                        className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center shadow">×</button>
                    </div>
                  ))}
                  <label className="aspect-video border-2 border-dashed border-gray-200 rounded-xl flex items-center justify-center cursor-pointer hover:border-blue-300 hover:bg-blue-50 transition-all">
                    <p className="text-xs font-bold text-gray-400">{yukleniyorId === tip.odaTipiId ? '...' : '+ Ekle'}</p>
                    <input type="file" accept="image/*" className="hidden" onChange={e => resimYukle(tip, e)} disabled={yukleniyorId !== null} />
                  </label>
                </div>
              </div>

              {/* Özellikler */}
              <div>
                <p className={LABEL}>Oda Özellikleri</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {ODA_OZELLIKLERI.map(oz => {
                    const secili = ozellikler.includes(oz.id);
                    return (
                      <button key={oz.id} type="button" onClick={() => ozellikToggle(tip, oz.id)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold border-2 transition-all text-left
                          ${secili ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-gray-100 bg-white text-gray-500 hover:border-gray-200'}`}>
                        <span className={secili ? 'text-blue-500' : 'text-gray-400'}>{ODA_OZELLIK_IKONLARI[oz.id]}</span>
                        <span>{oz.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

            </div>
          </div>
        );
      })}
      <p className="text-xs text-gray-400">Değişiklikler anında kaydedilir.</p>
    </div>
  );
}

function ReferanslarYonetici({ referanslarJson, onChange }) {
  const [yeniAd, setYeniAd] = useState('');
  const [yeniYorum, setYeniYorum] = useState('');
  const [yeniPuan, setYeniPuan] = useState(5);
  const [yeniTarih, setYeniTarih] = useState('');

  let liste = [];
  try { liste = JSON.parse(referanslarJson || '[]'); } catch { liste = []; }

  function sil(idx) {
    onChange(JSON.stringify(liste.filter((_, i) => i !== idx)));
  }

  function ekle() {
    if (!yeniAd.trim() || !yeniYorum.trim()) return;
    const yeni = [...liste, { ad: yeniAd.trim(), yorum: yeniYorum.trim(), puan: yeniPuan, tarih: yeniTarih.trim() || undefined }];
    onChange(JSON.stringify(yeni));
    setYeniAd(''); setYeniYorum(''); setYeniPuan(5); setYeniTarih('');
  }

  return (
    <div className="space-y-4">
      {/* Mevcut Yorumlar */}
      {liste.length > 0 && (
        <div className="space-y-3">
          {liste.map((r, idx) => (
            <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold text-gray-800">{r.ad}</span>
                  <span className="text-amber-400 text-sm">{'★'.repeat(r.puan || 5)}</span>
                  {r.tarih && <span className="text-xs text-gray-400">{r.tarih}</span>}
                </div>
                <p className="text-xs text-gray-600 leading-relaxed line-clamp-2">{r.yorum}</p>
              </div>
              <button onClick={() => sil(idx)} className="text-red-400 hover:text-red-600 text-xs px-2 flex-shrink-0">Sil</button>
            </div>
          ))}
        </div>
      )}

      {/* Yeni Yorum Ekle */}
      <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 space-y-3">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Yeni Yorum Ekle</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <input className={INP} value={yeniAd} placeholder="Ad Soyad *" onChange={e => setYeniAd(e.target.value)} />
          </div>
          <div>
            <input className={INP} value={yeniTarih} placeholder="Tarih (örn: Mayıs 2025)" onChange={e => setYeniTarih(e.target.value)} />
          </div>
        </div>
        <textarea className={`${INP} resize-none`} rows={2} value={yeniYorum}
          placeholder="Misafir yorumu *" onChange={e => setYeniYorum(e.target.value)} />
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map(y => (
              <button key={y} type="button" onClick={() => setYeniPuan(y)}
                className={`text-xl transition-colors ${y <= yeniPuan ? 'text-amber-400' : 'text-gray-200 hover:text-amber-300'}`}>★</button>
            ))}
          </div>
          <button onClick={ekle} disabled={!yeniAd.trim() || !yeniYorum.trim()}
            className="px-4 py-2 rounded-xl text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 transition-all">
            Ekle
          </button>
        </div>
      </div>
    </div>
  );
}

function GaleriYonetici({ galeriJson, onChange }) {
  const [yukleniyor, setYukleniyor] = useState(false);

  let liste = [];
  try { liste = JSON.parse(galeriJson || '[]'); } catch { liste = []; }

  function sil(idx) {
    const yeni = liste.filter((_, i) => i !== idx);
    onChange(JSON.stringify(yeni));
  }

  async function yukle(e) {
    const dosyalar = Array.from(e.target.files);
    if (!dosyalar.length) return;
    setYukleniyor(true);
    const yeniListe = [...liste];
    for (const dosya of dosyalar) {
      try {
        const fd = new FormData();
        fd.append('dosya', dosya);
        const res = await fetch(`${API_URL}/website/galeri`, {
          method: 'POST',
          credentials: 'include',
          body: fd,
        });
        if (res.ok) {
          const data = await res.json();
          yeniListe.push(data.url);
        }
      } catch { }
    }
    onChange(JSON.stringify(yeniListe));
    setYukleniyor(false);
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        {liste.map((url, idx) => (
          <div key={idx} className="relative group">
            <img src={getResimUrl(url)} alt="" className="w-full h-24 object-cover rounded-xl border border-gray-200" />
            <button onClick={() => sil(idx)}
              className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center shadow">
              ×
            </button>
          </div>
        ))}
        <label className="h-24 border-2 border-dashed border-gray-200 rounded-xl flex items-center justify-center cursor-pointer hover:border-blue-300 hover:bg-blue-50 transition-all">
          <div className="text-center">
            <p className="text-xs font-bold text-gray-400">{yukleniyor ? 'Yükleniyor...' : '+ Ekle'}</p>
          </div>
          <input type="file" accept="image/*" multiple className="hidden" onChange={yukle} disabled={yukleniyor} />
        </label>
      </div>
      <p className="text-xs text-gray-400">Birden fazla görsel aynı anda seçilebilir. Max 5 MB.</p>
    </div>
  );
}
